import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useRef, useState } from 'react';
import { isAfter, isValid, parse } from 'date-fns';
import { useI18n } from '@springbig/shared';

export type CreditCardDto = {
    name?: string;
    number?: string;
    expiry?: string;
    cvv?: string;
    zip?: string;
};

type UseCreditCardFormProps = {
    onCancel?: () => void;
    onProceed: (data: CreditCardDto) => void;
    isLoading?: boolean;
};

type StateNames = 'isAmExCard' | 'isNumberVisible' | 'isNumberFieldFocused';

const useCreditCardForm = ({
    onProceed,
}: UseCreditCardFormProps) => {

    const [isAmExCard, setIsAmExCard] =
        useState<boolean>(false);

    const [isNumberVisible, setIsNumberVisible] =
        useState<boolean>(false);

    const [isNumberFieldFocused, setIsNumberFieldFocused] =
        useState<boolean>(false);

    const numberFieldRef = useRef(null);

    const { t } = useI18n();

    const setBooleanState = (
        stateName: StateNames,
        value: boolean
    ): void => {

        switch (stateName) {
        case 'isAmExCard':
            setIsAmExCard(value);
            break;
        case 'isNumberVisible':
            setIsNumberVisible(value);
            break;
        case 'isNumberFieldFocused':
            setIsNumberFieldFocused(value);
            break;
        default:
            console.log(`useCreditCardForm > setBooleanState > ${stateName} state does not exist`);
            break;
        }
    };

    type FormData = yup.InferType<typeof schema>;

    const schema = yup.object().shape({
        name: yup
            .string()
            .required(t('validation.name_required'))
            .trim()
            .max(255, t('validation.name_long'))
            .matches(/^[\p{L}' -]+$/u, t('validation.name_invalid')),
        number: yup
            .string()
            .required(t('validation.number_required'))
            .trim()
            .min(14, t('validation.number_short'))
            .max(19, t('validation.number_long'))
            .matches(/^\d{13,19}$/, t('validation.number_invalid')),
        zip: yup
            .string()
            .required(t('validation.zip_required'))
            .trim()
            .matches(
                /^(\d{5}(?:[-\s]\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/,
                t('validation.zip_invalid')
            ),
        cvv: yup
            .string()
            .required(t('validation.cvv_required'))
            .trim()
            .matches(/^[0-9]{3,4}$/, t('validation.cvv_invalid')),
        expiry: yup
            .string()
            .required(t('validation.expiry_required'))
            .trim()
            .length(5, t('validation.expiry_invalid')),
    });

    const { control, handleSubmit, setError, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const onSubmit = (data: FormData): void => {

        const parsedDate = parse(data.expiry, 'MM/yy', new Date());

        if ((isAfter(new Date(), parsedDate) || !isValid(parsedDate))) {
            setError('expiry', {
                type: 'manual',
                message: t('validation.expiry_invalid'),
            });
        }
        else {
            clearErrors('expiry');
            onProceed(data);
        }
    };

    const { field: nameField } = useController({
        control,
        defaultValue: '',
        name: 'name',
    });

    const { field: numberField } = useController({
        control,
        defaultValue: '',
        name: 'number',
    });

    const { field: expiryField } = useController({
        control,
        defaultValue: '',
        name: 'expiry',
    });

    const { field: cvvField } = useController({
        control,
        defaultValue: '',
        name: 'cvv',
    });

    const { field: zipField } = useController({
        control,
        defaultValue: '',
        name: 'zip',
    });

    const onCardNumberChange = (value: string): void => {

        setIsAmExCard(['34', '37'].includes(value.slice(0, 2)));
        const numericValue = value.replace(/[^0-9]/g, '');
        numberField.onChange(numericValue);
    };

    const onExpiryChange = (value: string): void => {

        clearErrors('expiry');
        const numericValue = value.replace(/\D/g, '');

        const formattedValue = numericValue.replace(/^(.{2})/, '$1/');
        const limitedValue = formattedValue.slice(0, 5);

        expiryField.onChange(limitedValue);

        const parsedDate = parse(limitedValue, 'MM/yy', new Date());

        if (limitedValue.length === 5 && (isAfter(new Date(), parsedDate) || !isValid(parsedDate))) {
            setError('expiry', {
                type: 'manual',
                message: t('validation.expiry_invalid'),
            });
        }
        else {
            clearErrors('expiry');
        }
    };

    return {
        isAmExCard,
        isNumberVisible,
        isNumberFieldFocused,
        numberFieldRef,
        nameField,
        numberField,
        cvvField,
        expiryField,
        zipField,
        onCardNumberChange,
        onExpiryChange,
        setBooleanState,
        onSubmit,
        formMethods: {
            control,
            handleSubmit,
            setError,
            clearErrors,
            errors
        }
    };
};

export default useCreditCardForm;
